import { CommonModule, NgClass } from '@angular/common'
import { Component, computed, input, Input, signal, ViewChild } from '@angular/core'
import { SimpleSize } from '@engineering11/ui-lib/e11-ui-config'
import { CJobPatternToCategory } from '../c-job/c-job.model'
import { ITraitDots } from '../core/culture-index.model'
import { CultureIndexChartCore } from './culture-index-chart-core.component'
import { generateRandomTraitDots } from './utils/culture-index-chart.util'

/**
 * Culture Index Chart
 * @example
 * ```html
  <culture-index-chart
    containerClass="e11-ml-auto"
    [traitDots]="traitDots"
    [compareTraitDots]="compareTraitDots"
    [loading]="loading"
  ></culture-index-chart>
  ```
 */
@Component({
  selector: 'culture-index-chart',
  // changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgClass, CultureIndexChartCore, CommonModule],
  template: `
    <div
      class=" e11-w-full e11-aspect-[16/8] {{ containerClass }}"
      [ngClass]="{
        'e11-max-w-[528px]': size === 'md',
        'e11-max-w-[295px]': size === 'sm',
        'e11-animate-pulse': loading
      }"
    >
      <div class="e11-flex e11-items-center e11-justify-between">
        <h4 *ngIf="title" class="e11-ml-4 e11-font-bold">{{ title }}</h4>
        <button
          *ngIf="displayComparisonToggle"
          type="button"
          (click)="toggleComparison()"
          class="e11-flex e11-text-skin-primary-accent e11-items-center"
        >
          <span class="material-icons-outlined"> swap_horiz </span>{{ _hideComparison() ? 'Show comparison' : 'Hide comparison' }}
        </button>
      </div>

      <culture-index-chart-core
        #cultureIndexChartCore
        [size]="size"
        [loading]="loading"
        [traitDots]="computedTraitDots()"
        [compareTraitDots]="computedCompareTraitDots()"
        [euText]="getEuText(computedTraitDots())"
      ></culture-index-chart-core>

      <!-- footer -->
      <div *ngIf="hasFooter" class="e11-flex e11-items-center e11-justify-between e11-px-8">
        <div class="e11-flex e11-items-center">
          <span>Ideal:</span>
          <span class="e11-flex e11-items-center e11-ml-2" [ngClass]="{ 'e11-skeleton e11-text-transparent': !_loading }">
            <span class="e11-w-4 e11-h-4 e11-border e11-rounded-full e11-inline-block" [style.background-color]="idealPattern?.color"></span>
            <span class="e11-ml-2">{{ idealPattern?.pattern ?? 'No Pattern' }}</span>
          </span>
        </div>
        <div class="e11-flex e11-items-center">
          <span>Candidate:</span>
          <span class="e11-flex e11-items-center e11-ml-2" [ngClass]="{ 'e11-skeleton e11-text-transparent': !_loading }">
            <span class="e11-w-4 e11-h-4 e11-border e11-rounded-full e11-inline-block" [style.background-color]="candidatePattern?.color"></span>
            <span class="e11-ml-2">{{ candidatePattern?.pattern ?? 'No Pattern' }}</span>
          </span>
        </div>
      </div>
    </div>
  `,
})
export class CultureIndexChart {
  @ViewChild('cultureIndexChartCore') cultureIndexChartCore!: CultureIndexChartCore
  @Input() size: Omit<SimpleSize, 'lg'> = 'md'

  _loading = signal(false)
  _hideComparison = signal(false)
  @Input() set loading(value: boolean) {
    this._loading.set(value)
    if (value) this.startLoadingAnimation()
    else this.stopLoadingAnimation()
  }

  loadingAnimation = input<boolean>(true)

  get loading() {
    return this._loading()
  }

  @Input() containerClass?: string
  @Input() title?: string
  @Input() displayComparisonToggle?: boolean = false
  @Input() hasFooter?: boolean = false
  @Input() idealPattern?: CJobPatternToCategory
  @Input() candidatePattern?: CJobPatternToCategory
  traitDots = input<ITraitDots>()
  compareTraitDots = input<ITraitDots>()

  computedTraitDots = computed(() => {
    const isLoading = this.loading
    const isAnimating = this.loadingAnimation()
    const hasTraitDots = !!this.traitDots()
    if (isLoading) {
      if (isAnimating || !hasTraitDots) {
        return this.loadingTraitDots()
      }

      return this.traitDots()
    }

    return this.traitDots()
  })

  computedCompareTraitDots = computed(() => {
    if (this._hideComparison()) return undefined
    const isLoading = this.loading
    const isAnimating = this.loadingAnimation()
    const hasTraitDots = !!this.traitDots()

    if (isLoading) {
      if (isAnimating || !hasTraitDots) {
        return this.loadingCompareTraitDots()
      }

      return this.compareTraitDots()
    }

    return this.compareTraitDots()
  })

  loadingTraitDots = signal(generateRandomTraitDots())
  loadingCompareTraitDots = signal(generateRandomTraitDots())

  loadingAnimationInterval: NodeJS.Timeout | undefined

  startLoadingAnimation() {
    if (!this.loadingAnimation()) return
    this.loadingAnimationInterval = setInterval(() => {
      this.setRandomPattern()
    }, 3000)
  }

  stopLoadingAnimation() {
    clearInterval(this.loadingAnimationInterval)
  }

  /**
   * Set the trait to random pattern for loading animation
   */
  setRandomPattern() {
    this.loadingTraitDots.set(generateRandomTraitDots())
    this.loadingCompareTraitDots.set(generateRandomTraitDots())
  }
  toggleComparison() {
    this._hideComparison.set(!this._hideComparison())
    setTimeout(() => this.cultureIndexChartCore?.draw())
  }
  getEuText(traitDots: ITraitDots | undefined) {
    return `EU = ${traitDots?.EU ?? '0 - 0'}`
  }
}
